import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import {
  addCartShippingData,
  addCartShippingDataV2,
} from 'src/sdk/checkout/data/checkout/shippingData'
import { useLocation } from '@reach/router'
import { fetchCepData } from 'src/utils/fetchCepData'
import { getCookie, setCookie } from 'src/utils/cookies'
import { navigate } from 'gatsby'
import { hasQuickFilters } from 'src/utils/plp/QuickFilters'
import { resolveAddressReferenceLabel } from 'src/components/common/Header/PostalCode/utils/addressReferenceLabel'
import { normalizePostalCode } from 'src/components/common/Header/PostalCode/utils/postalCode'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'

import type { Address } from '../types'
import {
  findResidentialAddressIfNonResidential,
  isMaskedPostalCode,
  isNonPersistableAddressType,
  isPersistablePostalCode,
  isValidAddress,
  isValidCEPSize,
  resolveJourneyPostalCode,
  unmaskCEP,
} from '../../utils'
import { EMPTY_ADDRESS } from '../variables'

export const checkHyphenInCEP = (cep: string) => {
  if (cep.includes('-')) {
    return cep
  }

  return `${cep.slice(0, 5)}-${cep.slice(5)}`
}

function normalizePostalCodeForState(value: string | undefined) {
  if (!value) {
    return undefined
  }

  const digits = normalizePostalCode(value)

  if (digits.length !== 8) {
    return value
  }

  return checkHyphenInCEP(digits)
}

function addressFromOrderForm(orderAddress: AddressData): Address {
  return {
    country: orderAddress?.country ?? 'BRA',
    street: orderAddress?.street ?? '',
    city: orderAddress?.city ?? '',
    state: orderAddress?.state ?? '',
    postalCode: orderAddress?.postalCode ?? '',
    neighborhood: orderAddress?.neighborhood ?? '',
    complement: orderAddress?.complement ?? '',
    number: orderAddress?.number ?? '',
    reference: orderAddress?.reference ?? '',
    receiverName: orderAddress?.receiverName ?? '',
    addressId: orderAddress?.addressId ?? '',
    addressType: orderAddress?.addressType ?? 'residential',
  }
}

function resolveInitialAddress(orderForm: OrderForm): Address {
  const orderAddress = orderForm?.shippingData?.address

  if (orderAddress && !isNonPersistableAddressType(orderAddress.addressType)) {
    return addressFromOrderForm(orderAddress)
  }

  const residentialAddress = findResidentialAddressIfNonResidential(orderForm)

  if (residentialAddress) {
    return addressFromOrderForm(residentialAddress)
  }

  if (orderAddress) {
    return addressFromOrderForm(orderAddress)
  }

  return EMPTY_ADDRESS
}

export const useAddressCheckout = () => {
  const { orderForm, setOrderForm } = useCheckout()

  const [postalCode, setPostalCodeState] = useState<string | undefined>(() =>
    resolveJourneyPostalCode(orderForm)
  )

  const setPostalCode = useCallback((value: string | undefined) => {
    if (value && isMaskedPostalCode(value)) {
      return
    }

    setPostalCodeState(normalizePostalCodeForState(value))
  }, [])

  // O initializer do useState roda no SSR, sem o cookie. Reidrata uma vez no cliente.
  useEffect(() => {
    const cookiePostalCode = getCookie('postalCode')

    if (!cookiePostalCode || !isPersistablePostalCode(cookiePostalCode)) {
      return
    }

    setPostalCodeState((current) => {
      if (isPersistablePostalCode(current)) {
        return current
      }

      return normalizePostalCodeForState(cookiePostalCode)
    })
  }, [])

  const { pathname, href } = useLocation()
  const [shippingMethod, setShippingMethod] = useState<string | null>(null)
  const [shippingData, setShippingData] = useState<any | null>(null)
  const [hasCompletedSelection, setHasCompletedSelection] = useState(false)
  const [selectedStore, setSelectedStore] = useState<any | null>(null)
  const [isOpenConfirmedAddress, setIsOpenConfirmedAddress] = useState(false)
  const [shouldShowWarningModal, setShouldShowWarningModal] = useState<{
    isOpen: boolean
    type: 'normal' | 'express' | null
    pickupEstimate: string | null
  }>({ isOpen: false, type: null, pickupEstimate: null })

  const [address, setAddress] = useState<Address>(() =>
    resolveInitialAddress(orderForm)
  )

  const previousAddressRef = useRef<Address | null>(address)
  const lastSyncedOrderAddressIdRef = useRef<string | null>(null)
  const lastPersistedPostalCodeRef = useRef<string | null>(null)
  const isResettingForNewAddressRef = useRef(false)

  const resetAddressForNewEntry = useCallback(() => {
    isResettingForNewAddressRef.current = true
    setAddress(EMPTY_ADDRESS)
  }, [])

  const isAddressDifferent = (a: Address, b: Address) =>
    [
      'street',
      'city',
      'state',
      'postalCode',
      'neighborhood',
      'number',
      'receiverName',
    ].some((key) => a[key as keyof Address] !== b[key as keyof Address])

  const hasAddressInfo = useMemo(
    () =>
      !!(
        address?.street &&
        address?.city &&
        address?.state &&
        address?.postalCode &&
        (address?.addressType === 'search' || address?.receiverName) &&
        address?.neighborhood &&
        address?.number
      ),
    [address]
  )

  useEffect(() => {
    if (isResettingForNewAddressRef.current) {
      return
    }

    const orderAddress = orderForm?.shippingData?.address
    const isNonPersistableAddress = isNonPersistableAddressType(
      orderAddress?.addressType
    )

    if (
      !orderAddress?.postalCode ||
      !isValidCEPSize(orderAddress.postalCode) ||
      isNonPersistableAddress
    ) {
      return
    }

    const orderAddressId = orderAddress.addressId ?? null

    if (orderAddressId === lastSyncedOrderAddressIdRef.current) {
      setPostalCode(orderAddress.postalCode)

      return
    }

    lastSyncedOrderAddressIdRef.current = orderAddressId

    const syncedAddress = addressFromOrderForm(orderAddress)

    setAddress(syncedAddress)
    setPostalCode(orderAddress.postalCode)
    previousAddressRef.current = syncedAddress
  }, [orderForm?.shippingData?.address, setPostalCode])

  useEffect(() => {
    if (isResettingForNewAddressRef.current) {
      if (address.street && address.city && address.postalCode) {
        isResettingForNewAddressRef.current = false
      }

      return
    }

    const orderAddress = orderForm?.shippingData?.address
    const isLocalAddressIncomplete =
      !address.street || !address.city || !address.postalCode

    if (!isLocalAddressIncomplete) {
      return
    }

    if (!orderAddress) {
      const residentialAddress =
        findResidentialAddressIfNonResidential(orderForm)

      if (!residentialAddress) {
        return
      }

      const checkoutAddress = addressFromOrderForm(residentialAddress)

      setAddress(checkoutAddress)
      setPostalCode(residentialAddress.postalCode ?? '')
      previousAddressRef.current = checkoutAddress

      return
    }

    if (isNonPersistableAddressType(orderAddress.addressType)) {
      if (!pathname.includes('checkout')) {
        return
      }

      const residentialAddress =
        findResidentialAddressIfNonResidential(orderForm)

      if (!residentialAddress) {
        return
      }

      const checkoutAddress = addressFromOrderForm(residentialAddress)

      setAddress(checkoutAddress)
      setPostalCode(residentialAddress.postalCode ?? '')
      previousAddressRef.current = checkoutAddress

      return
    }

    if (!orderAddress.postalCode || !isValidCEPSize(orderAddress.postalCode)) {
      return
    }

    const hydratedAddress = addressFromOrderForm(orderAddress)

    setAddress(hydratedAddress)
    setPostalCode(orderAddress.postalCode)
    previousAddressRef.current = hydratedAddress
    lastSyncedOrderAddressIdRef.current = orderAddress.addressId ?? null
  }, [
    address.city,
    address.postalCode,
    address.street,
    orderForm,
    orderForm?.shippingData?.address,
    orderForm?.shippingData?.availableAddresses,
    pathname,
    setPostalCode,
  ])

  useEffect(() => {
    const orderAddress = orderForm?.shippingData?.address
    const isCheckoutPage = pathname.includes('checkout')
    const isNotCartPage = pathname.includes('cart')
    const shouldUpdateAddress = isCheckoutPage && isNotCartPage

    if (!orderAddress || !isValidAddress(orderAddress as Address)) {
      return
    }

    const addressObject = addressFromOrderForm(orderAddress)

    const hasSignificantChange = isAddressDifferent(
      previousAddressRef.current as Address,
      addressObject as Address
    )

    if (isCheckoutPage && hasSignificantChange && shouldUpdateAddress) {
      if (!isNonPersistableAddressType(orderAddress?.addressType)) {
        setAddress(addressObject as Address)
        previousAddressRef.current = addressObject
      }

      return
    }

    if (!isNonPersistableAddressType(orderAddress?.addressType)) {
      previousAddressRef.current = addressObject
    }
  }, [orderForm?.shippingData?.address, pathname])

  const getCEP = useCallback(async (cep: string) => {
    const data = await fetchCepData(cep)

    if (data) {
      setAddress({ ...data })
      setIsOpenConfirmedAddress(false)
    } else {
      setAddress(EMPTY_ADDRESS)
    }

    return data
  }, [])

  useEffect(() => {
    const orderAddress = orderForm?.shippingData?.address

    if (isNonPersistableAddressType(orderAddress?.addressType)) {
      return
    }

    if (!isPersistablePostalCode(postalCode)) {
      return
    }

    const cookie = getCookie('postalCode')
    const normalizedPostalCode = normalizePostalCodeForState(postalCode)

    if (
      (cookie && cookie === normalizePostalCode(postalCode ?? '')) ||
      !postalCode
    ) {
      if (normalizedPostalCode && normalizedPostalCode !== postalCode) {
        setPostalCode(normalizedPostalCode)
      }

      return
    }

    if (cookie !== postalCode.split('-').join('')) {
      const url = new URL(href)

      if (hasQuickFilters(url.search)) {
        url.searchParams.set('zipCode', postalCode)
        url.searchParams.delete('pickupPoint')

        const facets = url.searchParams.get('facets')

        if (facets) {
          const sanitizedFacets = facets
            .split(',')
            .map((facet) => facet.trim())
            .filter((facet) => facet !== 'pickupPoint')

          if (sanitizedFacets.length > 0) {
            url.searchParams.set('facets', sanitizedFacets.join(','))
          } else {
            url.searchParams.delete('facets')
          }
        }

        navigate(`${url.pathname}?${url.searchParams.toString()}`, {
          replace: true,
        })
      }
    }

    setCookie('postalCode', postalCode.split('-').join(''))
  }, [
    postalCode,
    href,
    orderForm?.shippingData?.address,
    orderForm?.shippingData?.address?.addressType,
    pathname,
    setPostalCode,
  ])

  useEffect(() => {
    if (pathname.includes('checkout') || pathname.includes('/account')) {
      return
    }

    const orderAddress = orderForm?.shippingData?.address
    const availableAddresses = orderForm?.shippingData?.availableAddresses ?? []

    if (isNonPersistableAddressType(address.addressType)) {
      return
    }

    const localAddressId = address.addressId

    if (
      localAddressId &&
      !availableAddresses.some((item) => item.addressId === localAddressId)
    ) {
      const syncSource =
        orderAddress && !isNonPersistableAddressType(orderAddress.addressType)
          ? orderAddress
          : findResidentialAddressIfNonResidential(orderForm)

      if (syncSource) {
        const syncedAddress = addressFromOrderForm(syncSource)
        const shouldResync =
          (syncSource.addressId ?? null) !== localAddressId ||
          isAddressDifferent(address, syncedAddress)

        if (shouldResync) {
          setAddress(syncedAddress)
          setPostalCode(syncSource.postalCode ?? '')
          previousAddressRef.current = syncedAddress
          lastSyncedOrderAddressIdRef.current = syncSource.addressId ?? null

          return
        }
      }
    }

    if (
      orderAddress?.addressId &&
      orderAddress.addressId === address.addressId &&
      normalizePostalCode(orderAddress.postalCode) ===
        normalizePostalCode(postalCode ?? address.postalCode) &&
      !isNonPersistableAddressType(orderAddress.addressType)
    ) {
      return
    }

    const shouldRestoreResidentialAddress =
      isNonPersistableAddressType(orderAddress?.addressType) &&
      !isNonPersistableAddressType(address.addressType) &&
      isPersistablePostalCode(address.postalCode)

    const shouldSaveAddress =
      shouldRestoreResidentialAddress ||
      (isAddressDifferent(previousAddressRef.current as Address, address) &&
        isPersistablePostalCode(postalCode ?? address.postalCode))

    if (!shouldSaveAddress) {
      return
    }

    const cepToPersist = normalizePostalCode(
      postalCode ?? address.postalCode ?? ''
    )

    if (!cepToPersist || lastPersistedPostalCodeRef.current === cepToPersist) {
      return
    }

    lastPersistedPostalCodeRef.current = cepToPersist

    const addressObject: Address = {
      addressType: 'residential',
      receiverName: address.receiverName ?? '',
      postalCode: postalCode ? unmaskCEP(postalCode) : '',
      city: address.city ?? '',
      state: address.state ?? '',
      country: 'BRA',
      street: address.street ?? '',
      number: address.number ?? '',
      neighborhood: address.neighborhood ?? '',
      complement: address.complement ?? '',
      addressId: address?.addressId ?? '',
      reference: resolveAddressReferenceLabel(address),
    }

    const saveAddress = async () => {
      try {
        if (!orderForm?.orderFormId) {
          lastPersistedPostalCodeRef.current = null
          console.error(
            'orderForm or orderFormId is undefined when saving address'
          )

          return
        }

        const hasPersistableAddressFields =
          Boolean(addressObject.street?.trim()) &&
          Boolean(addressObject.number?.trim())

        const cep = unmaskCEP(postalCode ?? address.postalCode ?? '')

        const response = hasPersistableAddressFields
          ? await addCartShippingDataV2(orderForm.orderFormId, [addressObject])
          : await addCartShippingData(orderForm.orderFormId, cep)

        setOrderForm(response)
        previousAddressRef.current = addressObject
        lastSyncedOrderAddressIdRef.current = addressObject.addressId ?? null
        setPostalCode(postalCode ?? address.postalCode)
      } catch (err) {
        lastPersistedPostalCodeRef.current = null
        console.error({ err })
      }
    }

    saveAddress()
  }, [
    address,
    orderForm,
    orderForm?.orderFormId,
    orderForm?.shippingData?.address,
    pathname,
    postalCode,
    setOrderForm,
    setPostalCode,
  ])

  const openWarningModal = (
    type: 'normal' | 'express',
    pickupEstimate?: string | null
  ) =>
    setShouldShowWarningModal({
      isOpen: true,
      type,
      pickupEstimate: pickupEstimate ?? null,
    })

  const closeWarningModal = () =>
    setShouldShowWarningModal({
      isOpen: false,
      type: null,
      pickupEstimate: null,
    })

  return {
    postalCode,
    setPostalCode,
    address,
    setAddress,
    resetAddressForNewEntry,
    hasAddressInfo,
    shippingMethod,
    setShippingMethod,
    shippingData,
    setShippingData,
    hasCompletedSelection,
    setHasCompletedSelection,
    shouldShowWarningModal,
    openWarningModal,
    closeWarningModal,
    selectedStore,
    setSelectedStore,
    isOpenConfirmedAddress,
    setIsOpenConfirmedAddress,
    getCEP,
  }
}
